import { useId, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { messagesApi, type ConversationMessage, type ConversationSummary } from '../api/endpoints.ts';
import type { TFunction } from 'i18next';
import { ApiRequestError } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { useAiDisclosure } from './ai-disclosure.tsx';
import { Button } from './ui.tsx';
import { AI_DISCLOSURE_PROVIDERS } from '@shared/ai-disclosure.ts';
import { ANONYMOUS_CALLER, isTerminalCallStatus, isWrittenInbound, type InboxCall } from '@shared/voice.ts';
import { formatPhoneForDisplay } from '@shared/phone.ts';

/**
 * How long a call lasted, the same in the call's details and in the
 * customer's history. For a voicemail it is the message's length when the
 * recording reports one, since the call also counts the ringing and the
 * greeting. For any other call it is the call's own length.
 */
export function callDurationSeconds(call: InboxCall): number | null {
  const recorded = call.recording?.durationSeconds ?? null;
  return call.voicemail ? (recorded ?? call.durationSeconds) : (call.durationSeconds ?? recorded);
}

/** A call's length as the audio player shows it: "0:42", "5:05", "1:02:09". */
export function formatCallDuration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const rest = String(total % 60).padStart(2, '0');
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, '0')}:${rest}` : `${minutes}:${rest}`;
}

/**
 * Whether a person can answer this message in writing: an email, a text, or
 * a voicemail whose transcript is ready (answered by text).
 */
export function isAnswerableInbound(message: ConversationMessage): boolean {
  // A voicemail counts only with words in it: a silent one finishes with an
  // empty transcript, and a locked one hides its text.
  const transcript = message.call?.voicemail && message.call.transcript?.text ? message.call.transcript.status : null;
  return message.direction === 'inbound' && isWrittenInbound(message.channel, transcript);
}

/**
 * Whether a reply can actually be sent from here. A call on a thread with no
 * customer has no number the composer can text: an unknown caller's thread is
 * not a text conversation until somebody attaches it to a customer.
 */
export function canAnswerInbound(message: ConversationMessage, customerId: string | null | undefined): boolean {
  return isAnswerableInbound(message) && (message.channel !== 'call' || Boolean(customerId));
}

/** The channel a reply to this message goes out on. A voicemail is answered by text. */
export function replyChannelFor(message: ConversationMessage): 'email' | 'sms' {
  return message.channel === 'email' ? 'email' : 'sms';
}

/**
 * A conversation's preview as a list shows it. A call's message body is the
 * caller's number until a transcript replaces it, and a withheld or unreadable
 * caller ID is stored as `anonymous`: shown in words, as the call itself is.
 */
export function conversationPreviewText(
  conversation: Pick<ConversationSummary, 'preview' | 'lastChannel'>,
  t: TFunction,
): string | null {
  if (conversation.lastChannel === 'call' && conversation.preview === ANONYMOUS_CALLER)
    return t('voice.from', { number: t('voice.withheld') });
  return conversation.preview;
}

/**
 * A call's outcome in words. A call the office placed that the customer did
 * not pick up is "No answer", not "Missed call": the office missed nothing.
 */
export function callOutcomeLabel(call: InboxCall, t: TFunction): string {
  if (call.voicemail) return t('voice.voicemail');
  if (call.direction === 'outbound' && call.status === 'no-answer') return t('voice.noAnswer');
  return t(`voice.status.${call.status}`);
}

/**
 * A call the office placed that Twilio never took (refused, or never
 * reached): its message has no call record, only its status and the number.
 */
export function unplacedCallLabel(message: ConversationMessage, t: TFunction): string {
  return t(message.status === 'failed' ? 'voice.callNotStarted' : 'voice.callStarting', {
    number: formatPhoneForDisplay(message.body ?? ''),
  });
}

/** How often a thread is read again while a call the office placed is under way. */
export const CALL_UNDER_WAY_POLL_MS = 5000;

/** Past this, a call still "under way" lost its callbacks: polling would never see it end. */
const CALL_UNDER_WAY_MS = 60 * 60 * 1000;

/**
 * Whether these messages hold a call the office placed in the last hour that
 * has not ended (or not started yet). Twilio's callbacks move it on, and
 * nothing else tells an open page, so the page reads its messages again
 * until it ends.
 */
export function hasCallUnderWay(messages: readonly ConversationMessage[] | undefined, now = Date.now()): boolean {
  return (messages ?? []).some(
    (message) =>
      message.channel === 'call' &&
      message.direction === 'outbound' &&
      now - Date.parse(message.createdAt) < CALL_UNDER_WAY_MS &&
      (message.call ? !isTerminalCallStatus(message.call.status) : message.status === 'queued'),
  );
}

const WORKERS_AI = AI_DISCLOSURE_PROVIDERS.find((provider) => provider.id === 'cloudflare_workers_ai')?.name;

/**
 * A call in a thread: direction, outcome and length, and for a voicemail the
 * recording, its transcript and the suggested reply prepared from it.
 *
 * The audio link is short-lived and signed for this viewer by the server; a
 * plan that no longer includes calls keeps the recording but sends no link,
 * and this says why rather than showing a dead player.
 */
export function CallDetails({
  message,
  onUseDraft,
}: {
  message: ConversationMessage;
  /** Puts the suggested reply into the composer (Messages only). */
  onUseDraft?: (text: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const call = message.call;
  if (!call) return null;
  const from = call.from === ANONYMOUS_CALLER ? t('voice.withheld') : call.from;
  const duration = callDurationSeconds(call);
  return (
    <div className="text-sm" data-testid="call-details">
      <p className="font-medium">{callOutcomeLabel(call, t)}</p>
      {/* A call the office placed went to the customer's number, from the company's. */}
      <p>{call.direction === 'outbound' ? t('voice.to', { number: call.to }) : t('voice.from', { number: from })}</p>
      {duration !== null ? <p>{t('voice.duration', { length: formatCallDuration(duration) })}</p> : null}
      {call.recording ? (
        <Recording call={call} />
      ) : (
        <p className="mt-1 text-xs text-[color:var(--color-muted)]">{t('voice.noRecording')}</p>
      )}
      {call.transcript ? <Transcript call={call} language={i18n.language} /> : null}
      {/* Any call the server will retry: a voicemail, or a recorded answered call. */}
      {call.retryable ? <RetryCall call={call} /> : null}
      {call.aiReplyDraft ? (
        <section
          className="mt-2 rounded border border-[color:var(--color-line)] p-2"
          aria-label={t('voice.suggestedReply')}
        >
          <h4 className="text-xs font-semibold">{t('voice.suggestedReply')}</h4>
          <p className="whitespace-pre-wrap">{call.aiReplyDraft}</p>
          <p className="mt-1 text-xs text-[color:var(--color-muted)]">{t('voice.suggestedReplyHint')}</p>
          {onUseDraft ? (
            <Button variant="secondary" onClick={() => onUseDraft(call.aiReplyDraft!)}>
              {t('voice.useReply')}
            </Button>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}

function Recording({ call }: { call: InboxCall }) {
  const { t } = useTranslation();
  const recording = call.recording!;
  if (recording.url)
    return (
      // eslint-disable-next-line jsx-a11y/media-has-caption -- the transcript below is the caption
      <audio
        className="mt-2 w-full"
        controls
        preload="none"
        src={recording.url}
        aria-label={call.voicemail ? t('voice.playVoicemail') : t('voice.playRecording')}
      />
    );
  const note = recording.locked
    ? t('voice.recordingLocked')
    : recording.status === 'pending'
      ? t('voice.recordingSaving')
      : recording.status === 'failed'
        ? t('voice.recordingFailed')
        : t('voice.noAudio');
  return <p className="mt-1 text-xs text-[color:var(--color-muted)]">{note}</p>;
}

function Transcript({ call, language }: { call: InboxCall; language: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const id = useId();
  const transcript = call.transcript!;
  if (transcript.tooShort) return <p className="mt-1 text-xs text-[color:var(--color-muted)]">{t('voice.tooShort')}</p>;
  if (transcript.status === 'pending')
    return <p className="mt-1 text-xs text-[color:var(--color-muted)]">{t('voice.transcribing')}</p>;
  if (transcript.status === 'failed')
    return <p className="mt-1 text-xs text-[color:var(--color-danger)]">{t('voice.transcriptFailed')}</p>;
  if (transcript.status === 'none')
    return <p className="mt-1 text-xs text-[color:var(--color-muted)]">{t('voice.transcriptNone')}</p>;
  if (transcript.text === null)
    return <p className="mt-1 text-xs text-[color:var(--color-muted)]">{t('voice.recordingLocked')}</p>;
  if (!transcript.text) return <p className="mt-1 text-xs text-[color:var(--color-muted)]">{t('voice.noSpeech')}</p>;
  const spoken = transcript.language && !language.startsWith(transcript.language) ? transcript.language : null;
  return (
    <div className="mt-2">
      <button
        type="button"
        className="text-xs underline"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((value) => !value)}
      >
        {open ? t('voice.hideTranscript') : t('voice.showTranscript')}
      </button>
      {open ? (
        <div id={id}>
          <p className="mt-1 border-l-2 border-[color:var(--color-line)] pl-3 whitespace-pre-wrap">{transcript.text}</p>
          <p className="mt-1 text-xs text-[color:var(--color-muted)]">
            {t('voice.transcriptByAi', { provider: WORKERS_AI ?? 'Cloudflare Workers AI' })}
            {spoken
              ? ` ${t('voice.spokenIn', { language: t(`voice.languages.${spoken}`, { defaultValue: spoken }) })}`
              : ''}
          </p>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Retry: fetches the audio again if it never arrived (a voicemail or a
 * recorded answered call) and transcribes a voicemail again. A voicemail's
 * retry can spend an AI credit, so the notice is asked for first, the same as
 * a drafted reply; an answered call's needs none.
 */
function RetryCall({ call }: { call: InboxCall }) {
  const { t } = useTranslation();
  const session = useSession();
  const queryClient = useQueryClient();
  const disclosure = useAiDisclosure();
  const [error, setError] = useState<string | null>(null);
  // The server says, with the rule its Retry endpoint applies (stalled runs included).
  const retryable = Boolean(call.retryable);
  const retry = useMutation({
    mutationFn: async () => {
      // Only a voicemail's retry can transcribe; an answered call's recording
      // is only fetched again, so the AI notice is not asked for.
      if (call.voicemail) await disclosure.ensure(true);
      return messagesApi.retryCall(call.id);
    },
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['conversation-thread'] });
      await queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: (caught) => setError(caught instanceof ApiRequestError ? caught.message : t('voice.retryFailed')),
  });
  if (!retryable || !session.can('customers:write') || call.recording?.locked) return null;
  return (
    <div className="mt-2">
      {disclosure.dialog}
      <Button variant="secondary" busy={retry.isPending} onClick={() => retry.mutate()}>
        {call.recording?.status === 'stored' ? t('voice.retryTranscript') : t('voice.retryRecording')}
      </Button>
      {error ? (
        <p role="alert" className="mt-1 text-xs text-[color:var(--color-danger)]">
          {error}
        </p>
      ) : null}
    </div>
  );
}
